import * as React from "react";
import {CardBoxSmall} from "../../../components/CardBox";
import {Title} from "../../../components/Title";
import {Legend, LegendItem} from "../../../components/charts/Legend";
import {ColorUtils} from "../../../components/Utils/color/ColorUtils";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {AssetBaseWidgetTexts, CategoriesColors, WidgetResponse} from "../../DashboardContainer";
import {DashboardRequestsData} from "../../data/DashboardData";
import {JSONMapper} from "../../data/JSONMapper";
import {StackedBarChart, StackedBarChartRow} from "../charts/StackedBarChart";
import {FormatLocaleNumber} from "../DashboardUtils";
import {SiteTypes} from "./MarketWidget";
import {RestDataLoader} from "./RestDataLoader";

import "./style/AssetBaseDashboardWidget.scss";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface AssetBaseDashboardWidgetProps {
    data: WidgetResponse;
    texts: AssetBaseWidgetTexts;
    withTelemetryDevice: boolean;
    colorGenerator: ColorUtils;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    cardsData: AssetBaseCardsData;
    manufacturersData: EquipmentByManufacturer[];
    selectedItemKey: string;
}

export interface AssetBaseCardsData {
    equipmentTotal: number;
    equipmentOfManufacturers: number;
}

export interface EquipmentByManufacturer {
    manufacturerName: string;
    equipmentByType: DashboardRequestsData.EquipmentByType[];
}

export class AssetBaseDashboardWidget extends React.Component<AssetBaseDashboardWidgetProps, WidgetState> {

    public static getValueWithPercentage(value: number, total: number) {
        if (total === 0) {
            return FormatLocaleNumber(value) + " (0%)";
        }
        return FormatLocaleNumber(value) + " (" + Math.round((value / total * 100)) + "%)";
    }

    constructor(props: AssetBaseDashboardWidgetProps) {
        super(props);
        this.state = {
            ready: false,
            error: false,
            cardsData: null,
            manufacturersData: null,
            selectedItemKey: null,
        };
        this.setSelectedItem = this.setSelectedItem.bind(this);
    }

    public setSelectedItem(itemKey: string): void {
        this.setState({selectedItemKey: itemKey});
    }

    public ready() {
        if (this.state.cardsData != null && this.state.manufacturersData != null) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        RestDataLoader.loadData(
            "statistics/equipment/counting/placement",
            {
                withTelemetry: this.props.withTelemetryDevice,
            },

            (data: any) => {
                this.setState({cardsData: JSONMapper.jsonEquipmentAssetBaseCardsDataMapper(data.list)});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
        RestDataLoader.loadData(
            "statistics/equipment/counting/manufacturerAndType",
            {
                withTelemetry: this.props.withTelemetryDevice,
                placements: "manufacturer",
            },
            (data: any) => {
                this.setState({manufacturersData: JSONMapper.jsonEquipmentByManufacturerMapper(data.list)});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
    }

    public render() {
        if (this.state.error) {
            return null;
        }
        if (!this.state.ready) {
            return (
                <div className="asset-base-widget">
                    <VeBoxCard className={"home-dashboard-card"}>
                        <VeBoxCard.Body>
                            <Loader/>
                        </VeBoxCard.Body>
                    </VeBoxCard>
                </div>
            );
        }

        const manufacturersData: StackedBarChartRow[] = this.state.manufacturersData.map(
            (item, i) => {
                return {
                    label: item.manufacturerName,
                    subItems: item.equipmentByType.map(
                        (sitem, j) => {
                            return {
                                label: sitem.typeName,
                                code: sitem.typeGuid,
                                value: sitem.equipmentCount,
                                icon: sitem.typeLogo,
                                color: sitem.typeColor,
                            };
                        },
                    ),
                };
            },
        );

        const legendItems: LegendItem[] = [];
        let total = 0;
        // retrieve legend items from data
        manufacturersData.forEach((d) => {
            d.subItems.forEach((si) => {
                const legendItem = legendItems.filter((li) => li.code === si.code);
                if (legendItem.length === 0) {
                    legendItems.push({
                        name: si.label,
                        code: si.code,
                        color: si.color || this.props.colorGenerator.getMappedColor(
                            CategoriesColors.COMPONENT_CATEGORIES,
                            si.code,
                        ),
                        icon: si.icon,
                        value: si.value,
                    });
                } else {
                    legendItem[0].value += si.value;
                }
                total += si.value;
            });
        });
        legendItems.map((li) => li.percentage = StatisticsUtils.getPercentage(total, li.value));
        const stackedBarChartTexts = {totals: this.props.texts.totals};

        const content = (
            <VeBoxCard className="asset-base-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    className="asset-base-widget__header commercial-header space-between"
                    title={this.props.texts.title}
                    titleClassName={"lowercase capitalize"}
                    singleLineTitle={true}
                    back={
                        <WidgetBadge type={WidgetBadgeType.COMMERCIAL}/>
                    }
                />
                <VeBoxCard.Body className="asset-base-widget__content">
                    <div className="cards-container">
                        <div className="top-card-container">
                            <CardBoxSmall
                                title={this.props.texts.equipmentWithTelemetry}
                                value={FormatLocaleNumber(this.state.cardsData.equipmentTotal)}
                                bolder={true}
                                extended={true}
                                onClick={this.onCardClick()}
                            />
                        </div>
                        <div className="bottom-cards-container">
                            <CardBoxSmall
                                title={this.props.texts.equipmentAtManufacturers}
                                value={
                                    AssetBaseDashboardWidget.getValueWithPercentage(
                                        this.state.cardsData.equipmentOfManufacturers,
                                        this.state.cardsData.equipmentTotal,
                                    )
                                }
                                onClick={this.onCardClick(SiteTypes.FACTORY)}
                            />
                            <CardBoxSmall
                                title={this.props.texts.equipmentNotAtManufacturers}
                                value={
                                    AssetBaseDashboardWidget.getValueWithPercentage(
                                        this.state.cardsData.equipmentTotal
                                        - this.state.cardsData.equipmentOfManufacturers,
                                        this.state.cardsData.equipmentTotal,
                                    )
                                }
                                onClick={this.onCardClick(
                                    SiteTypes.MOBILE_SITE,
                                    SiteTypes.OFFICES,
                                    SiteTypes.OTHER,
                                    SiteTypes.POINT_OF_SALES,
                                    SiteTypes.UNREGISTERED_SITE,
                                    SiteTypes.WAREHOUSE,
                                )}
                            />
                        </div>
                    </div>
                    <Title
                        title={this.props.texts.manufacturersTitle}
                        explanation={this.props.texts.manufacturersTitleExplanation}
                    />
                    <div className="manufacturers-widget-content-scrollable">
                        <div className="manufacturers-widget-container">
                            <div className="manufacturers-widget__content">
                                <div className="stacked-bar-chart__legend__container">
                                    <Legend
                                        items={legendItems}
                                        selectedItemName={this.state.selectedItemKey}
                                        onItemHover={this.setSelectedItem}
                                        onClick={this.onItemClick}
                                        selectionKey={"code"}
                                        iconOptions={{show: true}}
                                        showValues={true}
                                        order={"desc"}
                                    />
                                </div>
                                <AutoSizer style={{width: "auto"}}>
                                    {({width, height}) =>
                                        <StackedBarChart
                                            width={width - 40}
                                            // height={size.height / 100 * 70}
                                            height={height * .6}
                                            direction={"horizontal"}
                                            showValuesLabels={true}
                                            addTotalsItem={false}
                                            data={manufacturersData}
                                            legendItems={legendItems}
                                            margin={{
                                                top: 5,
                                                bottom: 5,
                                                left: 30,
                                                right: 5,
                                            }}
                                            texts={stackedBarChartTexts}
                                            showAxesOptions={{
                                                x: {line: false, tickLine: false},
                                                y: {line: false, tickLine: false},
                                            }}
                                            legendOptions={{external: true}}
                                            selectedItem={this.state.selectedItemKey}
                                            onPathOver={this.setSelectedItem}
                                            onClick={this.onGraphClick}
                                        />}
                                </AutoSizer>
                            </div>
                        </div>
                    </div>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
        return (
            <div className="asset-base-widget">
                {content}
            </div>
        );
    }

    private onItemClick = (key): void => {
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            equipmentType: [key],
            sites: [SiteTypes.FACTORY],
            applyFilter: true,
        });
    }

    private onCardClick = (...sites: SiteTypes[]) => {
        return () => {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                sites,
                allTelemetries: this.props.withTelemetryDevice,
                applyFilter: true,
            });
        };
    }

    private onGraphClick = (data) => {
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            location: data.x,
            equipmentType: [data.y],
            sites: [SiteTypes.FACTORY],
            allTelemetries: this.props.withTelemetryDevice,
            applyFilter: true,
        });
    }
}
